import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import isEmpty from 'lodash/isEmpty';
import { DeviceInfo } from 'src/constants/LocalStorageKeys';
import { useDeviceInfo, writeDeviceInfo } from 'src/hooks/useDeviceInfo';
import { readNativeDeviceConfig, toDeviceInfo } from 'src/native/deviceConfig';

/**
 * This file provides a utility function that extracts the Hexnode MDM wildcards from the URL query string and persists them to localStorage.
 * See here for supported search params: https://www.hexnode.com/mobile-device-management/help/how-to-pass-device-and-user-information-using-the-wildcards-supported-by-hexnode-mdm/
 *
 * Example URL query params: ?serialNumber=%serialnumber%&phoneNumber=%phonenumber%&phoneNo=%phoneno%&imei=%imei%&meid=%meid%&udid=%udid%&deviceId=%deviceid%&phoneNumberSim1=%phonenumber_sim1%&phoneNumberSim2=%phonenumber_sim2%
 *
 * In the native shell the same values arrive from Hexnode managed configuration instead, read via the
 * DeviceConfig plugin. They are hydrated here so there is a single writer of this localStorage key,
 * and they win over the URL because a URL wildcard can arrive unsubstituted.
 */
const DeviceSearchParams = [
    'deviceId',
    'deviceNotes',
    'imei',
    'meid',
    'phoneNo',
    'phoneNumber',
    'phoneNumberSim1',
    'phoneNumberSim2',
    'serialNumber',
    'udid',
];
const DeviceAttributesSet = new Set(DeviceSearchParams);

export default function CaptureDeviceInfo() {
    const [searchParams, setSearchParams] = useSearchParams();

    const storedDeviceInfo = useDeviceInfo();

    useEffect(() => {
        const fromSearchParams = Array.from(searchParams.entries())
            .filter(([key]) => DeviceAttributesSet.has(key))
            .reduce((obj, [key, value]) => {
                // Remove any wildcard placeholders (i.e. "%....%") that haven't been replaced with actual values.
                // Also reject values still containing '%' — Hexnode wildcards like %deviceid% can be partially
                // URL-decoded by the browser (e.g. the characters %de get decoded as Þ), leaving "Þviceid%" which
                // the regex can't match.
                const cleanedValue = value.replace(/%[^%]+%/g, '').trim();
                if (cleanedValue && !cleanedValue.includes('%')) {
                    return { ...obj, [key]: cleanedValue };
                }
                return obj;
            }, {} as DeviceInfo);

        if (!isEmpty(fromSearchParams)) {
            writeDeviceInfo({ ...fromSearchParams, lastUpdatedTimestamp: Date.now() });
        }

        // Only navigate when there is something to remove
        if (DeviceSearchParams.some((key) => searchParams.has(key))) {
            setSearchParams(
                (params) => {
                    DeviceSearchParams.forEach((key) => params.delete(key));
                    return params;
                },
                { replace: true }
            );
        }

        // Guards the write below: the native read resolves after a bridge round-trip, by which time
        // this component may have unmounted.
        let cancelled = false;

        // No-ops in a browser; resolves to null there and on shells without the plugin.
        const hydrateFromNative = async () => {
            const config = await readNativeDeviceConfig();
            if (cancelled || !config) {
                return;
            }
            const fromNative = toDeviceInfo(config);
            if (isEmpty(fromNative)) {
                return;
            }
            writeDeviceInfo({
                ...storedDeviceInfo,
                ...fromSearchParams,
                ...fromNative,
                lastUpdatedTimestamp: Date.now(),
            });
        };

        hydrateFromNative();

        return () => {
            cancelled = true;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}
